import React from 'react'

import { Controller, Control, UseFormSetValue, useWatch } from 'react-hook-form'

import { useTranslation } from '@/core/i18n/useTranslation'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { Textarea } from '@/shared/components/atoms/TextArea'
import { sortSubcategories } from '@/shared/utils/businessCategoryUtils'

import SelectableBadge from '../SelectableBadge'

type BrandSectionFormValues = {
	parent_business_category_id: number | null
	business_category_id: number | null
	business_category_description?: string | null
}

interface BrandSectionProps {
	control: Control<BrandSectionFormValues>
	setValue: UseFormSetValue<BrandSectionFormValues>
	businessCategories: Array<{
		id: number
		name: string
		parentId?: number | null
		requiresDescription?: boolean
		tags?: string[]
	}>
	isLoadingBusinessCategories?: boolean
	brandTags: Array<{ id: number; display: string }>
	selectedBrandTags: number[]
	onBrandTagToggle: (id: number) => void
}

export const BrandSection: React.FC<BrandSectionProps> = ({
	control,
	setValue,
	businessCategories,
	isLoadingBusinessCategories = false,
	brandTags,
	selectedBrandTags,
	onBrandTagToggle,
}) => {
	const { t } = useTranslation('common')
	const selectedBusinessCategoryId = useWatch({
		control,
		name: 'business_category_id',
	})

	const selectedSubcategory = businessCategories.find(
		(c) => c.id === selectedBusinessCategoryId
	)
	const requiresDescription = selectedSubcategory?.requiresDescription ?? false

	const filteredSubcategories = sortSubcategories(
		businessCategories.filter(
			(c) =>
				c.parentId !== null &&
				c.parentId !== undefined &&
				!c.tags?.includes('hide')
		)
	)

	return (
		<div className="space-y-6 max-w-[784px]">
			<h2 className="text-lg font-semibold">{t('account.brand.title')}</h2>

			<div className="flex flex-col lg:flex-col gap-4">
				<div className="flex flex-col gap-2 flex-1">
					<Label className="text-sm font-medium">
						{t('account.brand.business_category')}
					</Label>
					{isLoadingBusinessCategories ? (
						<Skeleton className="h-9 w-full bg-border dark:bg-accent" />
					) : (
						<Controller
							control={control}
							name="business_category_id"
							render={({ field, fieldState: { error } }) => (
								<div>
									<Select
										value={field.value ? String(field.value) : ''}
										onValueChange={(value) => {
											const category = businessCategories.find(
												(c) => c.id === Number(value)
											)
											setValue(
												'parent_business_category_id',
												category?.parentId ?? null
											)
											setValue('business_category_description', null)
											field.onChange(Number(value))
										}}
									>
										<SelectTrigger className="w-full">
											<SelectValue
												placeholder={t(
													'account.brand.select_business_category'
												)}
											/>
										</SelectTrigger>
										<SelectContent>
											{filteredSubcategories.map((category) => (
												<SelectItem
													key={category.id}
													value={String(category.id)}
												>
													{category.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>
					)}
				</div>
			</div>

			{requiresDescription && (
				<div className="flex flex-col gap-2">
					<Label className="text-sm font-medium">
						{t('account.brand.business_category_description')}
					</Label>
					<p className="text-xs text-muted-foreground">
						{t('account.brand.business_category_description_help')}
					</p>
					<Controller
						control={control}
						name="business_category_description"
						render={({ field, fieldState: { error } }) => (
							<div>
								<Textarea
									{...field}
									value={field.value ?? ''}
									placeholder={t(
										'account.brand.business_category_description_placeholder'
									)}
									maxLength={100}
									rows={3}
								/>
								<div className="flex justify-between mt-1">
									{error ? (
										<ErrorMessage error={error.message ?? ''} />
									) : (
										<span />
									)}
									<span className="text-xs text-muted-foreground ml-auto">
										{(field.value ?? '').length}/100
									</span>
								</div>
							</div>
						)}
					/>
				</div>
			)}

			<div className="flex flex-col gap-2">
				<div className="flex flex-col gap-2">
					<label className="text-sm font-medium">
						{t('account.brand.brand_profile')}
					</label>
					<div className="flex flex-wrap gap-2">
						{brandTags.map((tag) => (
							<SelectableBadge
								key={tag.id}
								tag={tag.display}
								isSelected={selectedBrandTags.includes(tag.id)}
								onClick={() => onBrandTagToggle(tag.id)}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}
